import { format } from 'date-fns';
import capitalize from 'lodash/capitalize';
import React from 'react';
import {
    BadgeLabel,
    type BadgeLabelVariant,
    BodySmallEmphasized,
    CheckIcon,
    IconButton,
    SquarePenIcon,
    TooltipTrigger,
    XStack,
    YStack,
} from 'smoothie';

interface MoneyMapperNavMapFooterProps {
    isExpanded: boolean;
    displayMoneyMapTitle: string;
    canEditName: boolean;
    onPressEditName: () => void;
    statusText: string;
    statusBadgeVariant: BadgeLabelVariant;
    updatedAt: string | Date | null | undefined;
    isSavingMoneyMap: boolean;
}

const formatAutoSavedTime = (
    updatedAt: string | Date | null | undefined,
): string => {
    if (updatedAt == null) {
        return '';
    }
    try {
        const d =
            typeof updatedAt === 'string' ? new Date(updatedAt) : updatedAt;
        if (Number.isNaN(d.getTime())) {
            return '';
        }
        return format(d, 'h:mm a');
    } catch {
        return '';
    }
};

export const MoneyMapperNavMapFooter = ({
    isExpanded,
    displayMoneyMapTitle,
    canEditName,
    onPressEditName,
    statusText,
    statusBadgeVariant,
    updatedAt,
    isSavingMoneyMap,
}: MoneyMapperNavMapFooterProps) => {
    const timePart = formatAutoSavedTime(updatedAt);
    const autoSavedLabel = isSavingMoneyMap
        ? 'Saving…'
        : timePart.length > 0
          ? `Auto-saved ${timePart}`
          : 'Auto-saved';

    const collapsedTooltip = `${capitalize(statusText)} · ${autoSavedLabel}`;

    if (!isExpanded) {
        return (
            <YStack alignItems="center" width="100%" gap={10}>
                <TooltipTrigger
                    tooltipContent={collapsedTooltip}
                    tooltipGroupName="money-mapper-left-nav"
                >
                    <IconButton
                        variant="tertiary"
                        size="tiny"
                        icon={SquarePenIcon}
                        onPress={onPressEditName}
                        disabled={!canEditName}
                    />
                </TooltipTrigger>
            </YStack>
        );
    }

    return (
        <YStack gap={12} width="100%">
            <XStack alignItems="flex-start" gap={8} width="100%">
                <BodySmallEmphasized
                    color="$gray1200"
                    flex={1}
                    numberOfLines={2}
                >
                    {displayMoneyMapTitle}
                </BodySmallEmphasized>
                {canEditName && (
                    <IconButton
                        variant="tertiary"
                        size="regular"
                        icon={SquarePenIcon}
                        onPress={onPressEditName}
                    />
                )}
            </XStack>
            <XStack gap={8} alignItems="center" width="100%">
                <BadgeLabel
                    size="regular"
                    text={capitalize(statusText)}
                    variant={statusBadgeVariant}
                />
                <BadgeLabel
                    size="regular"
                    text={autoSavedLabel}
                    variant="neutral"
                    icon={CheckIcon}
                />
            </XStack>
        </YStack>
    );
};
